import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, X, MessageSquare } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessageSearch = ({ onClose, onSelectConversation }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        searchMessages();
      } else {
        setResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const searchMessages = async () => {
    setSearching(true);
    try {
      const response = await axios.get(`${API}/messages/search`, {
        params: { q: searchQuery }
      });
      setResults(response.data);
    } catch (error) {
      console.error('Error searching messages:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectResult = (result) => {
    onSelectConversation(result.conversation);
    onClose();
  };

  const highlightText = (text, query) => {
    if (!text || !query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={i} className="bg-yellow-200 font-semibold">{part}</span>
      ) : (
        part
      )
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      style={{ pointerEvents: 'auto' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        style={{ pointerEvents: 'auto' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Search Messages
          </h2>
          <button
            data-testid="close-search-button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-black/5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#9CA3AF' }} />
          <input
            data-testid="search-messages-input"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search in all conversations..."
            autoFocus
            className="w-full pl-12 pr-4 py-3 rounded-full border outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/20"
            style={{ background: '#F9F9FB', borderColor: '#E5E7EB' }}
          />
        </div>

        <div className="flex-1 overflow-y-auto space-y-2">
          {searching ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : results.length > 0 ? (
            results.map((result, idx) => (
              <button
                key={idx}
                data-testid={`search-result-${idx}`}
                onClick={() => handleSelectResult(result)}
                className="w-full p-4 rounded-xl hover:bg-black/5 text-left transition-all"
                style={{ border: '1px solid rgba(0,0,0,0.05)' }}
              >
                <div className="flex items-start gap-3">
                  <MessageSquare className="w-5 h-5 mt-1" style={{ color: '#3B82F6' }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-semibold text-sm" style={{ color: '#111111' }}>
                        {result.conversation.is_group
                          ? result.conversation.name
                          : result.conversation.participants.find((p) => p.name !== result.message.sender_name)?.name}
                      </p>
                      <span className="text-xs" style={{ color: '#9CA3AF' }}>
                        {formatDistanceToNow(new Date(result.message.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-sm mb-1" style={{ color: '#6B7280' }}>
                      <span className="font-medium">{result.message.sender_name}:</span>{' '}
                      {highlightText(result.message.content, searchQuery)}
                    </p>
                  </div>
                </div>
              </button>
            ))
          ) : searchQuery.trim().length >= 2 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Search className="w-12 h-12 mb-3" style={{ color: '#D1D5DB' }} />
              <p className="text-sm font-medium" style={{ color: '#6B7280' }}>
                No messages found for "{searchQuery}"
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Search className="w-12 h-12 mb-3" style={{ color: '#D1D5DB' }} />
              <p className="text-sm font-medium" style={{ color: '#6B7280' }}>
                Type at least 2 characters to search
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MessageSearch;